import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe, Copy, CheckCircle, Warning } from "@phosphor-icons/react";

/**
 * Custom-Domain UI: shows current domain, DNS instructions, save/remove.
 * Props: tenant, onSaved(updatedTenant)
 */
export default function CustomDomainPanel({ tenant, onSaved }) {
  const [domain, setDomain] = useState(tenant?.custom_domain || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => { setDomain(tenant?.custom_domain || ""); }, [tenant]);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}/custom-domain`, { custom_domain: domain });
      onSaved?.(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Custom-Domain wirklich entfernen? Endkunden können dann nur noch über die Tonneva-URL zugreifen.")) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}/custom-domain`, { custom_domain: "" });
      setDomain(""); onSaved?.(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const tonnevaHost = (typeof window !== "undefined" ? window.location.hostname : "tonneva.de").replace(/^www\./, "");

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6 max-w-2xl">
      <div className="flex items-center gap-2 mb-1">
        <Globe size={20} weight="bold" style={{ color: "var(--tonneva-primary)" }} />
        <h3 className="font-heading text-xl font-bold tracking-tight">Eigene Domain</h3>
      </div>
      <p className="text-sm text-zinc-500 mb-5">Verbinden Sie Ihre eigene <code className="font-mono text-xs">.de</code>-Domain mit Ihrem Tonneva-Portal — z. B. <code className="font-mono text-xs">meine-tonnenreinigung.de</code>.</p>

      <div className="flex gap-2 mb-2">
        <Input className="h-11 rounded-md flex-1" placeholder="meine-tonnenreinigung.de" value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="custom-domain-input" />
        <Button onClick={save} disabled={busy || !domain} className="rounded-md text-white" style={{ background: "var(--tonneva-gradient)" }} data-testid="custom-domain-save">Speichern</Button>
        {tenant?.custom_domain && <Button onClick={remove} variant="outline" className="rounded-md" data-testid="custom-domain-remove">Entfernen</Button>}
      </div>
      {err && <div className="text-sm text-red-600 bg-red-50 rounded-md p-2.5 mb-3" data-testid="custom-domain-error">{err}</div>}

      <div className="mt-4 p-4 rounded-lg" style={{ background: "var(--tonneva-gradient-soft, #f0fdf4)" }}>
        <div className="flex items-center gap-2 mb-3">
          {tenant?.custom_domain && tenant?.custom_domain_verified ? (
            <><CheckCircle size={18} weight="bold" style={{ color: "#16A34A" }} /><span className="font-semibold text-sm">Domain ist aktiv</span></>
          ) : tenant?.custom_domain ? (
            <><Warning size={18} weight="bold" style={{ color: "#F59E0B" }} /><span className="font-semibold text-sm">DNS-Konfiguration ausstehend</span></>
          ) : (
            <><Globe size={18} weight="bold" style={{ color: "var(--tonneva-primary, #2A86A8)" }} /><span className="font-semibold text-sm">DNS-Records (nach dem Speichern)</span></>
          )}
        </div>
        <p className="text-xs text-zinc-700 mb-3">
          Fügen Sie diese DNS-Records bei Ihrem Domain-Provider (z. B. IONOS, Strato, GoDaddy) hinzu:
        </p>
        <div className="space-y-2">
          <DnsRow type="CNAME" host="@" value={tonnevaHost} copied={copied === "cname"} onCopy={() => copy(tonnevaHost, "cname")} testId="dns-cname-copy" />
          <DnsRow type="CNAME" host="www" value={tonnevaHost} copied={copied === "www"} onCopy={() => copy(tonnevaHost, "www")} testId="dns-www-copy" />
          <DnsRow type="TXT" host={`_tonneva-verify.${tenant?.custom_domain || domain || "ihre-domain.de"}`} value={`tonneva-tenant=${tenant?.tenant_id || ""}`} copied={copied === "txt"} onCopy={() => copy(`tonneva-tenant=${tenant?.tenant_id || ""}`, "txt")} testId="dns-txt-copy" />
        </div>
        <details className="mt-4 text-xs text-zinc-600">
          <summary className="cursor-pointer font-semibold">SSL-Zertifikat und Aktivierung</summary>
          <div className="mt-2 space-y-1 leading-relaxed">
            <p>Nach erfolgreicher DNS-Propagierung (kann bis zu 48h dauern) provisioniert Tonneva automatisch ein <b>Let's Encrypt SSL-Zertifikat</b> für Ihre Domain.</p>
            <p>Der Status wird hier auf <b>aktiv</b> aktualisiert, sobald die Konfiguration verifiziert ist.</p>
            <p className="text-zinc-500 italic">Hinweis: In der aktuellen Preview-Umgebung ist das automatische Cert-Provisioning deaktiviert. Im Production-Setup mit eigenem nginx/Caddy/Traefik wird das Zertifikat automatisch ausgestellt.</p>
          </div>
        </details>
      </div>
    </div>
  );
}

const DnsRow = ({ type, host, value, copied, onCopy, testId }) => (
  <div className="grid grid-cols-12 gap-2 items-center text-xs bg-white rounded-md border border-black/10 p-2.5">
    <div className="col-span-2"><div className="text-[9px] uppercase tracking-widest text-zinc-400">Typ</div><div className="font-mono font-bold">{type}</div></div>
    <div className="col-span-3"><div className="text-[9px] uppercase tracking-widest text-zinc-400">Host</div><div className="font-mono truncate">{host}</div></div>
    <div className="col-span-6"><div className="text-[9px] uppercase tracking-widest text-zinc-400">Wert</div><div className="font-mono truncate">{value}</div></div>
    <button onClick={onCopy} className="col-span-1 p-1.5 rounded-md hover:bg-zinc-100 flex justify-center" data-testid={testId}>
      {copied ? <CheckCircle size={14} weight="bold" style={{ color: "#16A34A" }} /> : <Copy size={14} weight="bold" />}
    </button>
  </div>
);
